import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { Localization } from "@viamap/viamap2-common";
import {
   aiFieldTone,
   formatAiFieldStatus,
   formatProvenanceChip,
   provenanceChipTone,
   type ANNServitutProvenance,
   type ServitutAiFieldStatus,
} from "src/modules/ANNModule/ANNServitutProvenance";
import type { ServitutAktSearchLocation, ServitutAktSearchSource } from "src/modules/ANNModule/servitutAktSearch";
import type { ServitutDocumentExtent, ServitutPageSourceStatus } from "./ServitutRedegoerelseRow";

export function AiRobotIcon() {
   return (
      <svg
         className="servitut-redegoerelse__aiRobot"
         width="14"
         height="14"
         viewBox="0 0 16 16"
         fill="none"
         aria-hidden="true"
         focusable="false"
      >
         <rect x="3" y="5.2" width="10" height="8.3" rx="2" stroke="currentColor" strokeWidth="1.35" />
         <path d="M8 5.2V2.6" stroke="currentColor" strokeWidth="1.35" strokeLinecap="round" />
         <circle cx="8" cy="2.15" r="1.05" stroke="currentColor" strokeWidth="1.2" />
         <circle cx="6.15" cy="8.75" r="1.05" fill="currentColor" />
         <circle cx="9.85" cy="8.75" r="1.05" fill="currentColor" />
         <path d="M6.2 11.45h3.6" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
      </svg>
   );
}

export function PersonIcon() {
   return (
      <svg
         className="servitut-redegoerelse__personIcon"
         width="14"
         height="14"
         viewBox="0 0 16 16"
         fill="none"
         aria-hidden="true"
         focusable="false"
      >
         <circle cx="8" cy="5.15" r="2.35" stroke="currentColor" strokeWidth="1.35" />
         <path
            d="M3.4 13.15c.55-2.55 2.2-3.9 4.6-3.9s4.05 1.35 4.6 3.9"
            stroke="currentColor"
            strokeWidth="1.35"
            strokeLinecap="round"
         />
      </svg>
   );
}

export function ServitutMarkChip(props: {
   text: string;
   tone?: "ok" | "warn" | "bad" | "neutral";
   title?: string;
   showAi?: boolean;
   showPerson?: boolean;
}) {
   if (!props.text) {
      return null;
   }
   const markClass = props.showPerson
      ? " servitut-redegoerelse__markChip--person"
      : props.showAi
         ? " servitut-redegoerelse__markChip--ai"
         : "";
   return (
      <span
         className={`servitut-redegoerelse__markChip servitut-redegoerelse__markChip--${props.tone || "neutral"}${markClass}`}
         title={props.title || props.text}
         aria-label={props.showPerson || props.showAi ? props.text : undefined}
      >
         {props.showPerson ? <PersonIcon /> : props.showAi ? <AiRobotIcon /> : null}
         {props.text}
      </span>
   );
}

export function ServitutProvenanceChip(props: {
   provenance?: ANNServitutProvenance;
   hideAiGenerated?: boolean;
   hideFetchedFromAkt?: boolean;
}) {
   const text = formatProvenanceChip(props.provenance, {
      hideAiGenerated: props.hideAiGenerated,
      hideFetchedFromAkt: props.hideFetchedFromAkt,
   });
   if (!text) {
      return null;
   }
   return <ServitutMarkChip text={text} tone={provenanceChipTone(props.provenance)} />;
}

export function ServitutDigitalDocumentChip() {
   return (
      <ServitutMarkChip
         text={Localization.getText("Digital document")}
         tone="ok"
      />
   );
}

export function ServitutPageFindChip(props: { source: ServitutPageSourceStatus }) {
   if (props.source.kind === "hidden") {
      return null;
   }
   if (props.source.kind === "user_pages") {
      return (
         <ServitutMarkChip
            text={Localization.getFormattedText("User found {count} pages", { count: props.source.count })}
            tone="ok"
            showPerson
         />
      );
   }
   if (props.source.kind === "ai_pages") {
      if (!props.source.confident) {
         return (
            <ServitutMarkChip
               text={Localization.getFormattedText("AI found {count} pages, not confident", { count: props.source.count })}
               tone="warn"
               title={props.source.reason}
               showAi
            />
         );
      }
      return (
         <ServitutMarkChip
            text={Localization.getFormattedText("AI found {count} pages", { count: props.source.count })}
            tone="ok"
            title={props.source.reason}
            showAi
         />
      );
   }
   if (props.source.kind === "ai_none") {
      return (
         <ServitutMarkChip
            text={Localization.getText("AI found no pages")}
            tone="bad"
            showAi
         />
      );
   }
   return (
      <ServitutMarkChip
         text={Localization.getText("Page search not run")}
         tone="neutral"
         showAi
      />
   );
}

export function ServitutDocumentExtentChip(props: {
   extent: ServitutDocumentExtent;
}) {
   if (props.extent.kind !== "entire_akt") {
      return null;
   }
   return (
      <ServitutMarkChip
         text={Localization.getText("Entire akt")}
         tone="neutral"
      />
   );
}

export function ServitutImportedChip() {
   return (
      <ServitutMarkChip
         text={Localization.getText("Annotation imported")}
         tone="ok"
         title={Localization.getText("Using saved annotation PDF")}
      />
   );
}

export function ServitutAiFieldChip(props: { status?: ServitutAiFieldStatus; text?: string }) {
   const text = props.text || formatAiFieldStatus(props.status);
   return (
      <ServitutMarkChip
         text={text}
         tone={aiFieldTone(props.status)}
         showAi={props.status !== "person_approved" && props.status !== "not_run"}
      />
   );
}

function ServitutHoverWrap(props: {
   className: string;
   ariaLabel?: string;
   popoverClassName?: string;
   interactive?: boolean;
   children: ReactNode;
   popover: ReactNode;
}) {
   const wrapRef = useRef<HTMLSpanElement | null>(null);
   const [open, setOpen] = useState(false);
   const [pos, setPos] = useState({ top: 0, left: 0 });
   const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
   const closeDelayMs = props.interactive ? 280 : 80;

   const clearHideTimer = () => {
      if (hideTimerRef.current !== null) {
         clearTimeout(hideTimerRef.current);
         hideTimerRef.current = null;
      }
   };

   const updatePosition = () => {
      const el = wrapRef.current;
      if (!el) {
         return;
      }
      const r = el.getBoundingClientRect();
      setPos({ top: r.bottom + 4, left: r.left + r.width / 2 });
   };

   const openPopover = () => {
      clearHideTimer();
      updatePosition();
      setOpen(true);
   };

   const scheduleClose = () => {
      clearHideTimer();
      hideTimerRef.current = setTimeout(() => setOpen(false), closeDelayMs);
   };

   useLayoutEffect(() => {
      if (!open) {
         return;
      }
      updatePosition();
   }, [open, props.popover]);

   useEffect(() => {
      if (!open) {
         return;
      }
      const close = () => setOpen(false);
      window.addEventListener("scroll", close, true);
      window.addEventListener("resize", close);
      return () => {
         window.removeEventListener("scroll", close, true);
         window.removeEventListener("resize", close);
      };
   }, [open]);

   useEffect(() => () => clearHideTimer(), []);

   return (
      <>
         <span
            ref={wrapRef}
            className={props.className}
            aria-label={props.ariaLabel}
            onMouseEnter={openPopover}
            onMouseLeave={scheduleClose}
         >
            {props.children}
         </span>
         {open ? createPortal(
            <div
               className={props.popoverClassName || "servitut-redegoerelse__sourceHintPopover"}
               style={{ top: pos.top, left: pos.left }}
               role={props.interactive ? "dialog" : "tooltip"}
               onMouseEnter={clearHideTimer}
               onMouseLeave={scheduleClose}
               onPointerDown={clearHideTimer}
            >
               {props.popover}
            </div>,
            document.body,
         ) : null}
      </>
   );
}

export function ServitutSourceHint(props: {
   mark: string;
   text: string;
   tone: "ovrig" | "text" | "korrekt" | "annotation" | "rigsarkiv" | "missing";
}) {
   return (
      <ServitutHoverWrap
         className={`servitut-redegoerelse__sourceHint servitut-redegoerelse__sourceHint--${props.tone}`}
         ariaLabel={props.text}
         popover={props.text}
      >
         {props.mark}
      </ServitutHoverWrap>
   );
}

export function aktSearchLocationLabel(source: ServitutAktSearchSource): string {
   if (source === "korrektAkt") {
      return Localization.getText("Correct akt in text");
   }
   if (source === "servitutAkt") {
      return Localization.getText("Direct akt link from Tingbogen");
   }
   if (source === "textAkt") {
      return Localization.getText("Akt number in Tingbogen");
   }
   return Localization.getText("Other akt (øvrige akt)");
}

export function ServitutAktSourcePill(props: {
   aktId?: string | null;
   usedSource?: ServitutAktSearchSource;
   locations: ServitutAktSearchLocation[];
   fallbackLabel?: string;
   customUpload?: {
      used?: boolean;
      filename?: string;
      disabled?: boolean;
      busy?: boolean;
      onUpload?: () => void;
   };
}) {
   const displayAkt = props.aktId?.trim()
      || props.locations.find((location) => location.source === props.usedSource)?.aktId
      || props.locations.find((location) => location.aktId)?.aktId
      || (props.customUpload?.used ? props.customUpload.filename : "")
      || props.fallbackLabel
      || "";
   const used = Boolean(props.usedSource) || Boolean(props.customUpload?.used);
   const label = displayAkt || Localization.getText("Not found");
   const uploadUsed = Boolean(props.customUpload?.used);

   return (
      <ServitutHoverWrap
         className={`servitut-redegoerelse__aktSourcePill${used ? " servitut-redegoerelse__aktSourcePill--used" : ""}`}
         ariaLabel={label}
         interactive
         popoverClassName="servitut-redegoerelse__sourceHintPopover servitut-redegoerelse__aktSourcePopover"
         popover={(
            <>
               <ol className="servitut-redegoerelse__aktSourceList">
                  {props.locations.map((location, index) => {
                     const isUsed = !uploadUsed && location.source === props.usedSource;
                     return (
                        <li
                           key={location.source}
                           className={`servitut-redegoerelse__aktSourceRow${isUsed ? " servitut-redegoerelse__aktSourceRow--used" : ""}`}
                        >
                           <span className="servitut-redegoerelse__aktSourceRowIndex" aria-hidden="true">
                              {index + 1}.
                           </span>
                           <span className="servitut-redegoerelse__aktSourceRowLabel">
                              {aktSearchLocationLabel(location.source)}
                           </span>
                           <span className="servitut-redegoerelse__aktSourceRowValue">
                              {location.aktId || "—"}
                              {isUsed ? ` · ${Localization.getText("Used")}` : ""}
                           </span>
                        </li>
                     );
                  })}
               </ol>
               <div className="servitut-redegoerelse__aktSourceDivider" role="separator" />
               <div
                  className={`servitut-redegoerelse__aktSourceRow servitut-redegoerelse__aktSourceUpload${uploadUsed ? " servitut-redegoerelse__aktSourceRow--used" : ""}`}
               >
                  <span className="servitut-redegoerelse__aktSourceRowIndex" aria-hidden="true">
                     5.
                  </span>
                  <span className="servitut-redegoerelse__aktSourceRowLabel">
                     {Localization.getText("Upload document yourself")}
                  </span>
                  <span className="servitut-redegoerelse__aktSourceUploadValue">
                     {uploadUsed && props.customUpload?.filename ? (
                        <span className="servitut-redegoerelse__aktSourceRowValue">
                           {props.customUpload.filename}
                           {` · ${Localization.getText("Used")}`}
                        </span>
                     ) : null}
                     {props.customUpload?.onUpload ? (
                        <button
                           type="button"
                           className="servitut-redegoerelse__aktSourceUploadButton"
                           disabled={props.customUpload.disabled}
                           onClick={(event) => {
                              event.preventDefault();
                              event.stopPropagation();
                              props.customUpload?.onUpload?.();
                           }}
                        >
                           {props.customUpload.busy
                              ? Localization.getText("Loading...")
                              : Localization.getText("Upload PDF")}
                        </button>
                     ) : null}
                  </span>
               </div>
            </>
         )}
      >
         {label}
      </ServitutHoverWrap>
   );
}
